import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { Box, Breadcrumbs, CircularProgress, Typography } from "@mui/material";
import Div from "@jumbo/shared/Div";
import DSOKProductForm from "./DSOKProductForm";
import {
  generateToken,
  getCustomer,
  getDSOKCategories,
  getDSOKProducts,
  upsertDSOKProduct,
} from "app/services/api/management";
import { getPosWorkspacesDocument } from "app/services/api/accessSystemPosWorkspaces";
import { normalizeWorkspacesForUi } from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";
import { buildTicketPrintersGroupedFromWorkspacesData } from "./ticketPrintersFromPosWorkspaces";
import { useSnackbar } from "notistack";

function getKitchenPrintersGrouped(customer) {
  const raw =
    typeof customer?.dsokSettings === "object" ? customer.dsokSettings : null;
  const parsed =
    raw ??
    (typeof customer?.dsokSettings === "string"
      ? (() => {
          try {
            return JSON.parse(customer.dsokSettings);
          } catch (_) {
            return null;
          }
        })()
      : null);

  const ws = parsed?.customer?.workspaces;
  const workspacesArray = Array.isArray(ws?.data) ? ws.data : [];
  if (!workspacesArray.length) return [];

  return workspacesArray
    .map((workspace, index) => {
      if (!workspace || typeof workspace !== "object") {
        return null;
      }
      const workspaceUuid = workspace.uuid || String(index + 1);
      const workspaceName =
        (typeof workspace.name === "string" && workspace.name.trim()) ||
        workspaceUuid;

      const devices = Array.isArray(workspace.devices)
        ? workspace.devices
        : [];

      const printers = devices
        .filter(
          (d) =>
            d &&
            typeof d === "object" &&
            d.type === "kitchen",
        )
        .map((d, i) => {
          const deviceKey = String(i + 1);
          const deviceId = (d.deviceId && String(d.deviceId).trim()) || "";
          const uuid = (d.uuid && String(d.uuid).trim()) || "";
          const id = deviceId || uuid || `${workspaceUuid}-${deviceKey}`;
          return {
            id,
            name:
              (typeof d.name === "string" && d.name.trim()) || deviceKey,
            ipAddress:
              (typeof d.ipAddress === "string" && d.ipAddress.trim()) || "",
          };
        });

      if (!printers.length) {
        return null;
      }

      return {
        workspaceKey: workspaceUuid,
        workspaceName,
        printers,
      };
    })
    .filter(Boolean);
}

const DSOKProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customer: authCustomer } = getAuthUser();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [ticketPrintersGrouped, setTicketPrintersGrouped] = useState([]);
  const [ticketPrintersLoading, setTicketPrintersLoading] = useState(false);

  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];
  const dsokType = customer?.dsokType;
  const customerUiSettings =
    customer?.dsokSettings?.customer?.customerUISettings;
  const accessSystemEnabled =
    (customer?.dsokSettings?.settings?.accessSystem?.enabled ??
      customer?.dsokSettings?.customer?.customerUISettings?.enableAccessSystem) ===
    true;

  const currencyCode = useMemo(() => {
    switch (customer?.dsokSettings?.customer?.currencyCode) {
      case "EUR":
        return "€";
      case "USD":
        return "$";
      case "Kč":
        return "Kč";
      default:
        return "€";
    }
  }, [customer]);

  const loyaltyEnabled =
    customer?.dsokSettings?.settings?.loyaltySystem?.isEnabled === true;
  const loyaltyType =
    customer?.dsokSettings?.settings?.loyaltySystem?.type || "none";

  const crosssellActive =
    customer?.dsokSettings?.settings?.enableCrossSell ||
    customer?.dsokType === "gastrokiosk";

  const dsokCustomer = useMemo(() => {
    const raw = customer?.dsokSettings;
    if (typeof raw === "object") return raw?.customer;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw)?.customer;
      } catch (_) {
        return undefined;
      }
    }
    return undefined;
  }, [customer?.dsokSettings]);

  const datavisionPosEnabled = dsokCustomer?.datavisionPosEnabled === true;
  const kitchenPrintingEnabled = dsokCustomer?.kitchenPrintingEnabled === true;
  const kitchenPrintersGrouped = useMemo(
    () => getKitchenPrintersGrouped(customer),
    [customer],
  );

  useEffect(() => {
    const customerId = customer?.id;
    if (customerId == null || String(customerId).trim() === "") {
      setTicketPrintersGrouped([]);
      setTicketPrintersLoading(false);
      return undefined;
    }
    let cancelled = false;
    setTicketPrintersLoading(true);
    setTicketPrintersGrouped([]);
    (async () => {
      try {
        const accessToken = await generateToken(customerId);
        if (cancelled) return;
        if (!accessToken) {
          setTicketPrintersGrouped([]);
          return;
        }
        const doc = await getPosWorkspacesDocument({ accessToken });
        if (cancelled) return;
        const { data } = normalizeWorkspacesForUi(doc && typeof doc === "object" ? doc : {});
        const grouped = buildTicketPrintersGroupedFromWorkspacesData(data);
        if (!cancelled) setTicketPrintersGrouped(grouped);
      } catch {
        if (!cancelled) setTicketPrintersGrouped([]);
      } finally {
        if (!cancelled) setTicketPrintersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customer?.id]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const cust = await getCustomer(authCustomer?.url || "");
        setCustomer(cust);
        const cats = await getDSOKCategories();
        const prods = await getDSOKProducts();
        setCategories(
          Object.keys(cats || {}).map((id) => ({ ...cats[id] })) || []
        );
        setProducts(Object.keys(prods || {}).map((id) => ({ ...prods[id] })));
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const product = useMemo(() => {
    if (productId === "new") return undefined;
    return products?.find((p) => p?._id === productId);
  }, [products, productId]);

  const handleSubmit = async (value, options = {}) => {
    const stay = options?.stay === true;
    try {
      const res = await upsertDSOKProduct(value);
      enqueueSnackbar(t("notifications.wasSuccessful"), { variant: "success" });
      if (!stay) {
        navigate("/customer/dsok/products");
        return;
      }
      // Save & stay: reload products so the detail reflects the saved data.
      const prods = await getDSOKProducts();
      setProducts(Object.keys(prods || {}).map((id) => ({ ...prods[id] })));
      // A newly created product gets its own detail URL, so a subsequent save
      // updates the same record instead of inserting a duplicate. The route param
      // change remounts the form (see key) with the persisted _id.
      if (productId === "new") {
        const newId = res?.id != null ? String(res.id) : "";
        if (newId) {
          navigate(`/customer/dsok/products/${newId}`, { replace: true });
        }
      }
    } catch (e) {
      enqueueSnackbar(t("notifications.wasFailed"), { variant: "error" });
    }
  };

  if (loading) {
    return <CircularProgress sx={{ m: "5px 50%" }} />;
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 2,
          "& a": {
            color: "text.secondary",
            textDecoration: "none",
          },
          "& a:hover": {
            textDecoration: "underline",
          },
        }}
      >
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/products">
          {t("pages.dsokProducts.title")}
        </Link>
        <Typography color="text.primary">
          {productId === "new"
            ? t("pages.products.addProduct")
            : product?.productName?.[defaultLanguage] || product?.name || ""}
        </Typography>
      </Breadcrumbs>
      <Div
        sx={{
          display: "flex",
          width: "100%",
          p: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          <DSOKProductForm
            key={product?._id || "new"}
            title={
              productId === "new"
                ? t("pages.products.addProduct")
                : t("pages.products.editProduct")
            }
            product={product}
            onAddProduct={handleSubmit}
            onEditProduct={handleSubmit}
            onCancel={() => navigate("/customer/dsok/products")}
            productLanguages={languages}
            defaultLanguage={defaultLanguage}
            products={products}
            categories={categories}
            locations={customer?.dsokSettings?.customer?.locations}
            customerUiSettings={customerUiSettings}
            accessSystemEnabled={accessSystemEnabled}
            dsokType={dsokType}
            crosssellActive={crosssellActive}
            currencyCode={currencyCode}
            loyaltyEnabled={loyaltyEnabled}
            loyaltyType={loyaltyType}
            datavisionPosEnabled={datavisionPosEnabled}
            kitchenPrintingEnabled={kitchenPrintingEnabled}
            kitchenPrintersGrouped={kitchenPrintersGrouped}
            ticketPrintersGrouped={ticketPrintersGrouped}
            ticketPrintersLoading={ticketPrintersLoading}
          />
        </Box>
      </Div>
    </React.Fragment>
  );
};

export default DSOKProductDetail;
